import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Swal from "sweetalert2";
import {
  CATEGORIES,
  HAKLAI_FARM_REMAINDER_KEY,
  HAKLAI_ADD_MODES,
  HAKLAI_PTOR_TYPES,
  normalizeHaklaiCurrencyForDate
} from "../shared/haklaiConstants";
import {
  getHaklaiComputedAcquisitionByPart,
  getHaklaiFarmRemainderDisplayState,
  getHaklaiFarmRemainderPart,
  getHaklaiPartDisplayLabel,
  isHaklaiPre1997MinimalZabanRelevantForPart
} from "../shared/haklaiFlowHelpers";
import {
  getHaklaiAcquisitionDisplayDate,
  isHaklaiPre1997AcquisitionDateEligible
} from "../preCalc/logic/haklaiPreCalcHelpers";
import {
  HAKLAI_PROPERTY_DEPRECIATION_MODE
} from "../shared/haklaiDepreciationHelpers";
import {
  HAKLAI_DEPRECIATION_ACTIONS,
  HAKLAI_DEPRECIATION_DIALOG,
  HAKLAI_DEPRECIATION_LABELS,
  HAKLAI_DEPRECIATION_TOOLTIPS
} from "../shared/haklaiDepreciationTexts";
import HaklaiPropertyPartsTable from "./components/HaklaiPropertyPartsTable";
import { HaklaiPropertyPartRow } from "./components/HaklaiPropertyPartRows";
import HaklaiPropertyDepreciationDialog from "../shared/depreciation/HaklaiPropertyDepreciationDialog";
import HasTzinunOnPtorDialog from "../preCalc/components/HaklaiHasTzinunOnPtorDialog";
import haklaiHelpTexts from "../shared/haklaiHelpTexts";
import { getHaklaiPropertyDepreciationUiState } from "../shared/depreciation/haklaiPropertyDepreciationUiState";
import {
  getHaklaiSelectedPtor,
  sanitizeHaklaiPtorTypeForWay
} from "../shared/haklaiPtorHelpers";
import {
  buildPropertyPartGroups,
  buildPropertyPartsComputationState,
  buildPropertyPartsValidationOverlay,
  findNextResidentialSlot,
  getCategoryAddState,
  getCategoryRows,
  getPropertyPartRowValidationState,
  parsePropertyPartFieldValue
} from "./logic/haklaiPropertyPartsHelpers";
import {
  useHaklaiPropertyPartsStyles
} from "./haklaiPropertyPartsStyles";
import {
  getHaklaiPropertyPartAddAriaLabel,
  HAKLAI_PROPERTY_PART_LABELS
} from "./haklaiPropertyPartsTexts";
import useHaklaiPropertyPartsController from "./hooks/useHaklaiPropertyPartsController";
import HaklaiLockNotice from "../shared/ui/HaklaiLockNotice";
import CustomDialog from "../shared/ui/HaklaiCustomDialog";

/**
 * Owns the Haklai property-parts section.
 *
 * Responsibilities:
 * - renders residential house rows and the derived farm remainder row
 * - coordinates part validation, highlighting, and add-row behavior
 * - opens purchase depreciation details for each relevant part
 *
 * Row/table UI belongs in `components/`; category rules and derivations belong
 * in `logic/`; controller-only state belongs in `hooks/`.
 */

const afterNextPaint = (callback) => {
  if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
    callback();
    return;
  }
  window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
};
const PROPERTY_PARTS_SECTION_TRANSITION_MS = 180;

const getPropertyPartFieldDomName = (rowKey, fieldName) => {
  if (!rowKey || !fieldName) return "";
  if (fieldName === "value") return `${rowKey}_value`;
  if (fieldName === "valueWithoutZaban") return `${rowKey}_valueWithoutZaban`;
  if (fieldName === "purchaseDepreciation") return `${rowKey}_purchaseDepreciation`;
  return "";
};

const scrollFieldIntoView = (rootElement, fieldName) => {
  if (!rootElement || !fieldName) return false;
  const fieldElement = rootElement.querySelector(`[name="${fieldName}"], [data-field="${fieldName}"]`);
  if (!fieldElement) return false;
  const targetCell = fieldElement.closest("td");
  const scrollTarget = targetCell || fieldElement.closest(".MuiFormControl-root") || fieldElement;
  const horizontalScrollContainer = scrollTarget.closest(".MuiTableContainer-root");

  scrollTarget.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "nearest"
  });

  if (
    horizontalScrollContainer
    && horizontalScrollContainer.scrollWidth > horizontalScrollContainer.clientWidth
  ) {
    const containerRect = horizontalScrollContainer.getBoundingClientRect();
    const targetRect = scrollTarget.getBoundingClientRect();
    const delta =
      (targetRect.left + targetRect.width / 2)
      - (containerRect.left + containerRect.width / 2);

    if (Math.abs(delta) > 1) {
      horizontalScrollContainer.scrollBy({
        left: delta,
        behavior: "smooth"
      });
    }
  }

  const focusTarget =
    scrollTarget.querySelector?.('[role="button"], input:not([type="hidden"]), textarea, button, [tabindex]:not([tabindex="-1"])')
    || fieldElement;

  if (typeof focusTarget.focus === "function") {
    try {
      focusTarget.focus({ preventScroll: true });
    } catch (error) {
      focusTarget.focus();
    }
  }

  return true;
};

const EMPTY_ROW_FIELDS = Object.freeze({});

const getPtorOptionsForWay = (wayOfGettingProperty) => {
  const options = [
    {
      value: HAKLAI_PTOR_TYPES.NONE,
      label: HAKLAI_PROPERTY_PART_LABELS.noExemption
    },
    {
      value: HAKLAI_PTOR_TYPES.SINGLE,
      label: HAKLAI_PROPERTY_PART_LABELS.singleApartmentExemption
    }
  ];

  if (wayOfGettingProperty === "inheritance") {
    options.push({
      value: HAKLAI_PTOR_TYPES.INHERITANCE,
      label: HAKLAI_PROPERTY_PART_LABELS.inheritanceExemption
    });
  }

  return options;
};

const HaklaiPropertyPartsSection = React.memo(forwardRef((props, ref) => {
  const classes = useHaklaiPropertyPartsStyles();
  const sectionRootRef = useRef(null);
  const [activePurchaseDepreciationPartKey, setActivePurchaseDepreciationPartKey] = useState("");
  const [activePurchaseDepreciationMode, setActivePurchaseDepreciationMode] = useState("");
  const [pendingRentalDepreciationByPart, setPendingRentalDepreciationByPart] = useState({});
  const [tzinunQuestionDialogOpen, setTzinunQuestionDialogOpen] = useState(false);
  const [tzinunCheckerDialogOpen, setTzinunCheckerDialogOpen] = useState(false);
  const [activeTzinunPtorPartKey, setActiveTzinunPtorPartKey] = useState("");
  const handleAddRow = props.handleAddRow;
  const handlePreCalcOnChange = props.handlePreCalcOnChange;
  const handlePropertyPartOnChange = props.handlePropertyPartOnChange;
  const handlePurchaseDepreciationChange = props.handlePurchaseDepreciationChange;
  const parts = useMemo(() => props.propertyPartsData || {}, [props.propertyPartsData]);
  const totalPropertyCost = Number(props.totalPropertyCost) || 0;
  const totalAcquisitionCostValue = Number(props.totalAcquisitionCost);
  const totalAcquisitionCost = Number.isFinite(totalAcquisitionCostValue) && totalAcquisitionCostValue > 0
    ? totalAcquisitionCostValue
    : 0;
  const hasZaban = props.hasZaban === true;
  const legacyPtorType = props.ptorType || HAKLAI_PTOR_TYPES.NONE;
  const hasTzinunOnPtor = props.hasTzinunOnPtor === true;
  const selectedPtor = useMemo(() => getHaklaiSelectedPtor(parts, {
    wayOfGettingProperty: props.wayOfGettingProperty,
    ptorType: legacyPtorType
  }), [legacyPtorType, parts, props.wayOfGettingProperty]);
  const ptorOptions = useMemo(
    () => getPtorOptionsForWay(props.wayOfGettingProperty),
    [props.wayOfGettingProperty]
  );
  const groups = useMemo(() => buildPropertyPartGroups(parts), [parts]);
  const farmRemainderDisplayState = useMemo(
    () => getHaklaiFarmRemainderDisplayState(parts, totalPropertyCost, hasZaban),
    [hasZaban, parts, totalPropertyCost]
  );
  const farmRemainderRow = useMemo(() => ([
    HAKLAI_FARM_REMAINDER_KEY,
    {
      ...getHaklaiFarmRemainderPart(farmRemainderDisplayState.value),
      displayStatus: farmRemainderDisplayState.status
    }
  ]), [farmRemainderDisplayState]);
  const allRows = groups.allRows;
  const residentialRentalByPart = useMemo(
    () => props.residentialRentalByPart || {},
    [props.residentialRentalByPart]
  );
  const purchaseDepreciationByPart = useMemo(
    () => props.purchaseDepreciationByPart || {},
    [props.purchaseDepreciationByPart]
  );
  const markRentalDepreciationPending = useCallback((partKey) => {
    if (!partKey) return;
    setPendingRentalDepreciationByPart((prev) => (
      prev[partKey] ? prev : { ...prev, [partKey]: true }
    ));
  }, []);
  const clearPendingRentalDepreciation = useCallback((partKey) => {
    if (!partKey) return;
    setPendingRentalDepreciationByPart((prev) => {
      if (!prev[partKey]) return prev;
      const next = { ...prev };
      delete next[partKey];
      return next;
    });
  }, []);
  const purchaseDateValue = useMemo(() => getHaklaiAcquisitionDisplayDate({
    wayOfGettingProperty: props.wayOfGettingProperty,
    acquisitionDetails: props.acquisitionDetails || {}
  }), [props.acquisitionDetails, props.wayOfGettingProperty]);
  const purchaseDepreciationStartDate = props.purchaseDepreciationStartDate || purchaseDateValue;
  const purchaseCurrencyValue = useMemo(() => normalizeHaklaiCurrencyForDate(
    purchaseDateValue,
    props.acquisitionDetails?.deduCurrency || "newShekel"
  ), [props.acquisitionDetails?.deduCurrency, purchaseDateValue]);
  const computedAcquisitionByPart = useMemo(
    () => getHaklaiComputedAcquisitionByPart(parts, props.acquisitionDetails?.amount),
    [parts, props.acquisitionDetails?.amount]
  );

  useEffect(() => {
    setPendingRentalDepreciationByPart((prev) => {
      const next = { ...prev };
      let changed = false;

      Object.keys(next).forEach((partKey) => {
        const savedRentalProfile = residentialRentalByPart?.[partKey];
        const hasSavedRentalProfile = (
          savedRentalProfile
          && typeof savedRentalProfile === "object"
          && Object.keys(savedRentalProfile).length > 0
        );
        if (!parts[partKey] || hasSavedRentalProfile) {
          delete next[partKey];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [parts, residentialRentalByPart]);

  const controller = useHaklaiPropertyPartsController({
    expanded: props.expanded,
    locked: props.locked,
    parts
  });
  const {
    committedRows,
    touchedFieldsByRow,
    stickyHeaderEnabled,
    lockableContentRef,
    flashingRowKey,
    setStickyHeaderEnabled,
    setValueInputRef,
    setBuiltValueInputRef,
    setRowRef,
    touchRowField,
    commitAllRows,
    scrollToRow,
    flashRowWhenVisible
  } = controller;

  const rowValidationStateByKey = useMemo(() => allRows.reduce((acc, [rowKey, part]) => {
    acc[rowKey] = getPropertyPartRowValidationState({
      rowKey,
      part,
      categoryRows: getCategoryRows(groups, part.category),
      hasZaban
    });
    return acc;
  }, {}), [allRows, groups, hasZaban]);

  const getRowValidationState = useCallback((rowKey, part) => (
    rowValidationStateByKey[rowKey] || getPropertyPartRowValidationState({
      rowKey,
      part,
      categoryRows: getCategoryRows(groups, part.category),
      hasZaban
    })
  ), [groups, hasZaban, rowValidationStateByKey]);

  const computationState = useMemo(() => buildPropertyPartsComputationState({
    groups,
    totalPropertyCost,
    totalAcquisitionCost
  }), [groups, totalAcquisitionCost, totalPropertyCost]);

  // Pure overlay build — lives here (not inside the controller hook) so it
  // can read `computationState.runningSaleBalanceByKey` and emit the
  // "value overshoot" cell error in lockstep with the rest of the field
  // errors. Single source of truth for every reveal rule.
  const validationOverlay = useMemo(() => buildPropertyPartsValidationOverlay({
    groups,
    touchedFieldsByRow,
    committedRows,
    hasZaban,
    runningSaleBalanceByKey: computationState.runningSaleBalanceByKey,
    validationStateByKey: rowValidationStateByKey
  }), [
    committedRows,
    computationState.runningSaleBalanceByKey,
    groups,
    hasZaban,
    rowValidationStateByKey,
    touchedFieldsByRow
  ]);

  const revealRowField = useCallback((rowKey, fieldName = "") => {
    if (!rowKey) return false;
    if (fieldName) touchRowField(rowKey, fieldName);
    afterNextPaint(() => {
      if (fieldName) {
        const didScroll = scrollFieldIntoView(
          sectionRootRef.current,
          getPropertyPartFieldDomName(rowKey, fieldName)
        );
        if (!didScroll) scrollToRow(rowKey);
      } else {
        scrollToRow(rowKey);
      }
      flashRowWhenVisible(rowKey);
    });
    return true;
  }, [flashRowWhenVisible, scrollToRow, touchRowField]);

  const getFirstInvalidPropertyPartTarget = useCallback(() => {
    const renderableRows = groups.renderableRows || [];
    if (!renderableRows.length) return null;
    const overshootRowKey = renderableRows.find(
      ([rowKey]) => Number(computationState.runningSaleBalanceByKey?.[rowKey]) < 0
    )?.[0] || "";

    for (const [rowKey, part] of renderableRows) {
      if (rowKey === overshootRowKey) return { rowKey, fieldName: "value" };

      const rowValidationState = getRowValidationState(rowKey, part);
      if (rowValidationState.firstInvalidField) {
        return { rowKey, fieldName: rowValidationState.firstInvalidField };
      }
    }

    return null;
  }, [
    computationState.runningSaleBalanceByKey,
    getRowValidationState,
    groups.renderableRows
  ]);

  const revealFirstInvalidField = useCallback(() => {
    const target = getFirstInvalidPropertyPartTarget();
    if (!target) return false;
    return revealRowField(target.rowKey, target.fieldName);
  }, [getFirstInvalidPropertyPartTarget, revealRowField]);

  const revealValueIssue = useCallback((issue = {}) => {
    const rowKey = issue.rowKey || "";
    if (rowKey) {
      revealRowField(rowKey, "value");
      return true;
    }
    afterNextPaint(() => {
      sectionRootRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest"
      });
    });
    return true;
  }, [revealRowField]);

  const canAskPre1997Construction = useMemo(
    () => isHaklaiPre1997AcquisitionDateEligible({
      wayOfGettingProperty: props.wayOfGettingProperty,
      acquisitionDetails: props.acquisitionDetails || {}
    }),
    [props.acquisitionDetails, props.wayOfGettingProperty]
  );

  const getPurchaseDepreciationUiState = useCallback((partKey, part = {}) => (
    getHaklaiPropertyDepreciationUiState({
      partKey,
      part,
      residentialRentalByPart,
      purchaseDepreciationByPart,
      soldPropertyDate: props.soldPropertyDate || "",
      purchaseDepreciationStartDate,
      acquisitionAmount: computedAcquisitionByPart[partKey]
    })
  ), [
    computedAcquisitionByPart,
    props.soldPropertyDate,
    purchaseDepreciationByPart,
    purchaseDepreciationStartDate,
    residentialRentalByPart
  ]);

  const handleOpenPurchaseDepreciation = useCallback((partKey, part, state = null) => {
    const resolvedState = state || getPurchaseDepreciationUiState(partKey, part);
    if (!partKey || !resolvedState.isRelevant) return;
    setActivePurchaseDepreciationPartKey(partKey);
    setActivePurchaseDepreciationMode(resolvedState.primaryMode);
  }, [getPurchaseDepreciationUiState]);

  const handlePurchaseDepreciationDialogClose = useCallback(() => {
    if (
      activePurchaseDepreciationPartKey
      && activePurchaseDepreciationMode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
      && !residentialRentalByPart?.[activePurchaseDepreciationPartKey]
    ) {
      clearPendingRentalDepreciation(activePurchaseDepreciationPartKey);
    }
    setActivePurchaseDepreciationPartKey("");
    setActivePurchaseDepreciationMode("");
  }, [
    activePurchaseDepreciationMode,
    activePurchaseDepreciationPartKey,
    clearPendingRentalDepreciation,
    residentialRentalByPart
  ]);

  const handleRentalDepreciationToggle = useCallback((partKey, part, checked, state = null) => {
    const resolvedState = state || getPurchaseDepreciationUiState(partKey, part);
    if (!partKey || resolvedState.isResidentialAutoAllowed !== true) return;

    if (checked) {
      markRentalDepreciationPending(partKey);
      handleOpenPurchaseDepreciation(partKey, part, {
        ...resolvedState,
        primaryMode: HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
      });
      return;
    }

    if (!resolvedState.hasRentalConfigured) {
      clearPendingRentalDepreciation(partKey);
      return;
    }

    Swal.fire({
      title: HAKLAI_DEPRECIATION_DIALOG.clearRentalTitle,
      text: HAKLAI_DEPRECIATION_DIALOG.clearRentalText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: HAKLAI_DEPRECIATION_ACTIONS.delete,
      cancelButtonText: HAKLAI_DEPRECIATION_ACTIONS.cancel,
      reverseButtons: true
    }).then((result) => {
      if (!result.isConfirmed) return;
      clearPendingRentalDepreciation(partKey);
      handlePurchaseDepreciationChange?.(
        partKey,
        {},
        HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
      );
    });
  }, [
    clearPendingRentalDepreciation,
    getPurchaseDepreciationUiState,
    handleOpenPurchaseDepreciation,
    handlePurchaseDepreciationChange,
    markRentalDepreciationPending
  ]);

  const getFirstInvalidPurchaseDepreciationPartKey = useCallback(() => {
    for (const [rowKey, part] of groups.renderableRows || []) {
      const state = getPurchaseDepreciationUiState(rowKey, part);
      if (state.isResidentialAutoAllowed === true && state.hasInvalid) return rowKey;
    }
    return "";
  }, [getPurchaseDepreciationUiState, groups.renderableRows]);

  const scrollToFirstPurchaseDepreciationIssue = useCallback(() => {
    const rowKey = getFirstInvalidPurchaseDepreciationPartKey();
    if (!rowKey) return false;
    revealRowField(rowKey, "purchaseDepreciation");
    return true;
  }, [getFirstInvalidPurchaseDepreciationPartKey, revealRowField]);

  const scrollToSection = useCallback(() => {
    afterNextPaint(() => {
      sectionRootRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest"
      });
    });
    return true;
  }, []);

  // Expose a minimal imperative surface so the parent (HaklaiCalcView) can
  // progressively reveal validation when the user clicks Analyze/Save.
  useImperativeHandle(ref, () => ({
    commitAllRows,
    revealFirstInvalidField,
    revealValueIssue,
    scrollToFirstPurchaseDepreciationIssue,
    scrollToSection
  }), [
    commitAllRows,
    revealFirstInvalidField,
    revealValueIssue,
    scrollToFirstPurchaseDepreciationIssue,
    scrollToSection
  ]);
  const showBuiltValueColumn = hasZaban && groups.residentialRows.length > 0;
  const showZabanColumn = hasZaban;
  const hasPre1997ConstructionQuestion = useMemo(() => (
    canAskPre1997Construction
    && hasZaban
    && groups.residentialRows.some(([, part]) => (
      isHaklaiPre1997MinimalZabanRelevantForPart(part, hasZaban)
    ))
  ), [canAskPre1997Construction, groups.residentialRows, hasZaban]);

  const residentialAddState = useMemo(() => getCategoryAddState({
    categoryId: CATEGORIES.RESIDENTIAL,
    propertyPartsData: parts
  }), [parts]);

  const sectionAAddMode = residentialAddState.mode;

  // Guards against adding a row into a category that is already saturated.
  // The parent `handleAddRow(category)` is self-sufficient (it derives the
  // next key internally for every category), so we only need to confirm a
  // slot is free before delegating.
  const canAppendRowToCategory = useCallback((categoryId) => {
    if (categoryId === CATEGORIES.RESIDENTIAL) {
      return Boolean(findNextResidentialSlot(parts));
    }
    return false;
  }, [parts]);

  const handleAddClick = useCallback((categoryId) => {
    if (!categoryId) return;
    if (!canAppendRowToCategory(categoryId)) return;
    handleAddRow(categoryId);
  }, [canAppendRowToCategory, handleAddRow]);

  const handleFieldChange = useCallback((componentKey, fieldName, rawValue) => {
    const parsed = parsePropertyPartFieldValue(fieldName, rawValue);
    if (parsed.skip) return;
    handlePropertyPartOnChange(componentKey, fieldName, parsed.value);
    if (
      fieldName === "ptorType"
      && parsed.value === HAKLAI_PTOR_TYPES.SINGLE
      && props.wayOfGettingProperty === "gift"
    ) {
      setActiveTzinunPtorPartKey(componentKey);
      setTzinunQuestionDialogOpen(true);
    }
  }, [handlePropertyPartOnChange, props.wayOfGettingProperty]);

  const clearTzinunBlockedPtor = useCallback(() => {
    const partKey = activeTzinunPtorPartKey || selectedPtor.partKey;
    if (partKey) {
      handlePropertyPartOnChange?.(partKey, "ptorType", HAKLAI_PTOR_TYPES.NONE);
    }
    handlePreCalcOnChange?.({
      target: {
        name: "hasTzinunOnPtor",
        value: false
      }
    });
    setActiveTzinunPtorPartKey("");
  }, [
    activeTzinunPtorPartKey,
    handlePreCalcOnChange,
    handlePropertyPartOnChange,
    selectedPtor.partKey
  ]);

  const handleTzinunChange = useCallback((hasTzinun) => {
    if (hasTzinun) {
      clearTzinunBlockedPtor();
      setTzinunQuestionDialogOpen(false);
      return;
    }

    handlePreCalcOnChange?.({
      target: {
        name: "hasTzinunOnPtor",
        value: hasTzinun === true
      }
    });
  }, [clearTzinunBlockedPtor, handlePreCalcOnChange]);

  const handleOpenTzinunCheckerDialog = useCallback(() => {
    setTzinunQuestionDialogOpen(false);
    setTzinunCheckerDialogOpen(true);
  }, []);

  const handleTzinunDialogResults = useCallback((event) => {
    if (
      event?.target?.name === "hasTzinunOnPtor"
      && (event.target.value === true || event.target.value === "true")
    ) {
      clearTzinunBlockedPtor();
      return;
    }

    handlePreCalcOnChange?.(event);
  }, [clearTzinunBlockedPtor, handlePreCalcOnChange]);

  const getEffectiveRowPtorType = useCallback((rowKey, part = {}) => {
    const rowPtorType = sanitizeHaklaiPtorTypeForWay(
      part?.ptorType,
      props.wayOfGettingProperty
    );
    if (rowPtorType !== HAKLAI_PTOR_TYPES.NONE) return rowPtorType;
    if (selectedPtor.isLegacy && selectedPtor.partKey === rowKey) {
      return selectedPtor.ptorType;
    }
    return HAKLAI_PTOR_TYPES.NONE;
  }, [props.wayOfGettingProperty, selectedPtor]);

  const handleCategoryAddIntent = useCallback((categoryId, addState) => {
    if (addState.mode === HAKLAI_ADD_MODES.ADD) {
      handleAddClick(categoryId);
    }
  }, [handleAddClick]);

  const handleSectionAAddClick = useCallback(() => {
    handleCategoryAddIntent(CATEGORIES.RESIDENTIAL, residentialAddState);
  }, [handleCategoryAddIntent, residentialAddState]);

  const renderRow = useCallback((rowKey, part, sectionRowProps) => {
    const rowValidationState = getRowValidationState(rowKey, part);
    const baseCellErrors = validationOverlay.cellErrors[rowKey] || EMPTY_ROW_FIELDS;
    const depreciationState = getPurchaseDepreciationUiState(rowKey, part);
    const rowDepreciationState = (
      pendingRentalDepreciationByPart[rowKey] === true
      && depreciationState.isResidentialAutoAllowed === true
      && !depreciationState.hasRentalConfigured
    )
      ? { ...depreciationState, hasRentalSelected: true }
      : depreciationState;
    const purchaseDepreciationError = (
      rowDepreciationState.isResidentialAutoAllowed === true
      && rowDepreciationState.hasInvalid
    )
      ? rowDepreciationState.validationError || HAKLAI_DEPRECIATION_TOOLTIPS.rentalDepreciationNeedsReview
      : "";
    let rowCellErrors = baseCellErrors;
    if (purchaseDepreciationError) {
      rowCellErrors = { ...rowCellErrors, purchaseDepreciation: purchaseDepreciationError };
    }
    const showPre1997ConstructionQuestion = (
      canAskPre1997Construction
      && isHaklaiPre1997MinimalZabanRelevantForPart(part, hasZaban)
    );
    const rowPtorType = getEffectiveRowPtorType(rowKey, part);
    return (
      <HaklaiPropertyPartRow
        key={rowKey}
        classes={classes}
        rowKey={rowKey}
        part={part}
        hasZaban={hasZaban}
        ptorType={rowPtorType}
        ptorOptions={ptorOptions}
        canRemove={part.category !== CATEGORIES.RESIDENTIAL || groups.residentialRows.length > 1}
        rowValidationState={rowValidationState}
        showBuiltValueColumn={showBuiltValueColumn}
        showZabanColumn={showZabanColumn}
        showPre1997ConstructionQuestion={showPre1997ConstructionQuestion}
        depreciationState={rowDepreciationState}
        onOpenDepreciation={handleOpenPurchaseDepreciation}
        onRentalDepreciationToggle={handleRentalDepreciationToggle}
        onFieldChange={handleFieldChange}
        onFieldBlur={touchRowField}
        onRemove={props.handleRemoveRow}
        setValueInputRef={setValueInputRef}
        setBuiltValueInputRef={setBuiltValueInputRef}
        setRowRef={setRowRef}
        cellErrors={rowCellErrors}
        isFlashing={flashingRowKey === rowKey}
        {...sectionRowProps}
      />
    );
  }, [
    classes,
    canAskPre1997Construction,
    getPurchaseDepreciationUiState,
    getRowValidationState,
    getEffectiveRowPtorType,
    groups,
    handleFieldChange,
    handleOpenPurchaseDepreciation,
    handleRentalDepreciationToggle,
    hasZaban,
    pendingRentalDepreciationByPart,
    props.handleRemoveRow,
    ptorOptions,
    setBuiltValueInputRef,
    setRowRef,
    setValueInputRef,
    showBuiltValueColumn,
    showZabanColumn,
    touchRowField,
    flashingRowKey,
    validationOverlay
  ]);

  const sections = useMemo(() => [
    {
      sectionId: "A",
      sectionTitle: HAKLAI_PROPERTY_PART_LABELS.sectionA,
      addMode: sectionAAddMode,
      addAriaLabel: getHaklaiPropertyPartAddAriaLabel(
        HAKLAI_PROPERTY_PART_LABELS.sectionA,
        sectionAAddMode === HAKLAI_ADD_MODES.ADD
      ),
      onAddClick: handleSectionAAddClick,
      bodies: [{
        bodyKey: "residential",
        rows: groups.residentialRows
      }],
      renderRow
    },
    {
      sectionId: "farmRemainder",
      sectionTitle: HAKLAI_PROPERTY_PART_LABELS.farmRemainderSection,
      showAddButton: false,
      bodies: [{
        bodyKey: "farmRemainder",
        rows: [farmRemainderRow]
      }],
      renderRow
    }
  ], [
    farmRemainderRow,
    groups.residentialRows,
    handleSectionAAddClick,
    renderRow,
    sectionAAddMode
  ]);

  const activePurchaseDepreciationPart = activePurchaseDepreciationPartKey
    ? parts[activePurchaseDepreciationPartKey]
    : null;
  const activePurchaseDepreciationState = activePurchaseDepreciationPart
    ? getPurchaseDepreciationUiState(activePurchaseDepreciationPartKey, activePurchaseDepreciationPart)
    : null;
  const activePurchaseDepreciationValue = activePurchaseDepreciationMode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
    ? activePurchaseDepreciationState?.rentalDepreciation || {}
    : activePurchaseDepreciationState?.manualDepreciation || {};
  const activePurchaseDepreciationTitle = activePurchaseDepreciationMode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
    ? HAKLAI_DEPRECIATION_LABELS.residentialRentalDepreciation
    : HAKLAI_DEPRECIATION_LABELS.form11Depreciation;

  return (
    <Box ref={sectionRootRef} className={classes.section}>
      <Box className={classes.sectionHeader}>
        <ButtonBase className={classes.sectionHeaderButton} onClick={props.onToggleExpanded}>
          <Typography className={classes.sectionHeaderTitle}>
            {HAKLAI_PROPERTY_PART_LABELS.sectionTitle}
          </Typography>
          <ExpandMoreIcon
            className={[
              classes.sectionToggleIcon,
              props.expanded ? classes.sectionToggleIconExpanded : ""
            ].filter(Boolean).join(" ")}
          />
        </ButtonBase>
      </Box>

      <Collapse
        in={props.expanded}
        timeout={PROPERTY_PARTS_SECTION_TRANSITION_MS}
        unmountOnExit={false}
        onEnter={() => setStickyHeaderEnabled(false)}
        onEntered={() => setStickyHeaderEnabled(true)}
        onExit={() => setStickyHeaderEnabled(false)}
      >
        <Box className={classes.sectionContent}>
          <HaklaiLockNotice
            classes={classes}
            message={props.locked ? props.lockNotice?.message : ""}
          />

          <Box
            ref={lockableContentRef}
            className={props.locked ? classes.lockedSectionContent : ""}
            // React 17 does not know about the `inert` attribute, so passing a
            // boolean renders the literal word "true"/"false" into the DOM.
            // Passing an empty string (when locked) produces the correct
            // `inert=""` attribute, and `undefined` omits it entirely.
            // React 18+ natively supports `inert` as a boolean prop — when
            // this project upgrades, replace with `inert={props.locked}`.
            inert={props.locked ? "" : undefined}
          >
            <Grid container direction="column" spacing={2} alignItems="center">
              {/*
                `alignSelf: "stretch"` overrides the parent's `alignItems: center`
                for THIS item only. The table wrapper fills that stretched width,
                so the section background and table chrome stay the same width
                when optional זב"ן columns are toggled.
              */}
              <Grid item style={{ alignSelf: "stretch", minWidth: 0, maxWidth: "100%" }}>
                <HaklaiPropertyPartsTable
                  classes={classes}
                  stickyHeaderEnabled={stickyHeaderEnabled}
                  showBuiltValueColumn={showBuiltValueColumn}
                  showZabanColumn={showZabanColumn}
                  hasPre1997ConstructionQuestion={hasPre1997ConstructionQuestion}
                  sections={sections}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Collapse>
      {activePurchaseDepreciationPart ? (
        <HaklaiPropertyDepreciationDialog
          open
          onClose={handlePurchaseDepreciationDialogClose}
          part={activePurchaseDepreciationPart}
          displayLabel={`${activePurchaseDepreciationTitle} - ${getHaklaiPartDisplayLabel(
            [activePurchaseDepreciationPartKey, activePurchaseDepreciationPart],
            getCategoryRows(groups, activePurchaseDepreciationPart.category)
          )}`}
          depreciation={activePurchaseDepreciationValue}
          ownershipStartDate={purchaseDepreciationStartDate}
          soldPropertyDate={props.soldPropertyDate}
          acquisitionCurrency={purchaseCurrencyValue}
          modeOverride={activePurchaseDepreciationMode}
          onSave={(nextDepreciation) => handlePurchaseDepreciationChange?.(
            activePurchaseDepreciationPartKey,
            nextDepreciation,
            activePurchaseDepreciationMode
          )}
        />
      ) : null}
      <CustomDialog
        label={HAKLAI_PROPERTY_PART_LABELS.tzinunQuestion}
        open={tzinunQuestionDialogOpen}
        onClose={() => setTzinunQuestionDialogOpen(false)}
        maxWidth="xs"
      >
        <Box className={classes.tzinunQuestionDialog}>
          <Typography variant="body1" className={classes.tzinunQuestionDialogText}>
            {HAKLAI_PROPERTY_PART_LABELS.tzinunApartmentQuestion}
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              row
              name="hasTzinunOnPtor"
              value={Boolean(hasTzinunOnPtor).toString()}
              onChange={(event) => handleTzinunChange(event.target.value === "true")}
            >
              <FormControlLabel
                value="false"
                control={<Radio color="primary" />}
                label={HAKLAI_PROPERTY_PART_LABELS.no}
                labelPlacement="end"
              />
              <FormControlLabel
                value="true"
                control={<Radio color="primary" />}
                label={HAKLAI_PROPERTY_PART_LABELS.yes}
                labelPlacement="end"
              />
            </RadioGroup>
          </FormControl>
          <Typography variant="body2" className={classes.tzinunCheckerPrompt}>
            {HAKLAI_PROPERTY_PART_LABELS.tzinunCheckPrompt}
            {" "}
            <ButtonBase
              component="span"
              className={classes.tzinunCheckerLink}
              onClick={handleOpenTzinunCheckerDialog}
            >
              {HAKLAI_PROPERTY_PART_LABELS.checkTzinunEligibility}
            </ButtonBase>
          </Typography>
          <Box className={classes.tzinunQuestionDialogActions}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setTzinunQuestionDialogOpen(false)}
            >
              {HAKLAI_PROPERTY_PART_LABELS.approve}
            </Button>
          </Box>
        </Box>
      </CustomDialog>
      <HasTzinunOnPtorDialog
        soldPropertyDate={props.soldPropertyDate}
        open={tzinunCheckerDialogOpen}
        onClose={() => setTzinunCheckerDialogOpen(false)}
        helpTextsOverride={haklaiHelpTexts}
        requireSoldPropertyDate
        handleResults={handleTzinunDialogResults}
      />
    </Box>
  );
}));

HaklaiPropertyPartsSection.displayName = "HaklaiPropertyPartsSection";

export default HaklaiPropertyPartsSection;
